import React, { ComponentType, forwardRef, PropsWithChildren, useImperativeHandle, useState } from 'react'
import ReactDOM from 'react-dom'
import { BrowserRouter } from 'react-router-dom'
import { Provider } from 'react-redux'
import { UseModelContext } from 'umi'
import { ConfigProvider, Modal, ModalProps, Button } from 'antd'
import zhCN from 'antd/es/locale/zh_CN'
import { getStore } from '@/utils/dva'
import { Merge } from '@/utils/utilityTypes'
import ErrorComponent from '@/components/ErrorComponent'
import Action from '@/components/Action'

const ActionButton = Action(Button)

/** 定义配置类型 */
interface ModalConfig extends ModalProps {
    visible?: boolean
    destroyOnClose?: boolean
    getCustomContainer?: () => HTMLElement | null
    onCancel?: (...props: any[]) => void
    routerChangeClose?: boolean
}

interface CreateAPIReturn<T> {
    update: (newConfig: T) => void
    close: (...args: any[]) => void
    destroy: (...args: any[]) => void
}

interface RefAPIProps {
    visible: boolean
    onCancel: () => void
}

const _CreateApiDestroyFns: Array<() => void> = []

/**
 * 创建可销毁的API实例
 */
export const CreateApiDestroy = () => {
    while (_CreateApiDestroyFns.length) {
        let destroy = _CreateApiDestroyFns.pop()
        if (destroy) {
            destroy()
        }
    }
}

/**
 * 创建模态框API
 * @param Component - 要渲染的组件
 * @param options - 配置选项
 */
export const createAPI = <T,>(
    Component: ComponentType<Merge<ModalProps, T>>,
    options?: Merge<ModalConfig, PropsWithChildren<T>>,
): CreateAPIReturn<T> => {
    const config: ModalConfig = {
        visible: false,
        destroyOnClose: true,
        ...(options || {}),
    }

    let $root = document.createElement('div')

    let currentConfig: ModalConfig = {
        ...config,
        visible: true,
        onCancel(props) {
            close()
            config.onCancel?.(props)
        },
    }

    if (typeof config.getCustomContainer === 'function') {
        const $element = config.getCustomContainer()
        $element?.appendChild($root)
    } else {
        document.body.appendChild($root)
    }

    /**
     * 渲染组件
     * @param props - 组件属性
     */
    function render<T>(props: Merge<ModalConfig, T>): void {
        ReactDOM.render(
            <ErrorComponent hideFallbackContent anchor="createAPI FallbackComponent">
                <ConfigProvider locale={zhCN}>
                    <Provider store={getStore()}>
                        <BrowserRouter>
                            <UseModelContext isCreateAPI>
                                <Component {...props} />
                            </UseModelContext>
                        </BrowserRouter>
                    </Provider>
                </ConfigProvider>
            </ErrorComponent>,
            $root,
        )
    }

    /**
     * 销毁组件
     * @param args - 参数
     */
    function destroy(...args: any[]): void {
        const unmountResult = ReactDOM.unmountComponentAtNode($root)
        if (unmountResult && $root.parentNode) {
            $root.parentNode.removeChild($root)
        }

        const triggerCancel = args.some(param => param?.triggerCancel)
        if (currentConfig.onCancel && triggerCancel) {
            currentConfig.onCancel(...args)
        }
    }

    /**
     * 更新配置
     * @param newConfig - 新配置
     */
    const update: CreateAPIReturn<T>['update'] = (newConfig): void => {
        currentConfig = {
            ...currentConfig,
            ...newConfig,
        }
        render(currentConfig)
    }

    /**
     * 关闭模态框
     * @param args - 参数
     */
    function close(...args: any[]): void {
        const { afterClose, destroyOnClose } = currentConfig

        currentConfig = {
            ...currentConfig,
            visible: false,
            afterClose() {
                if (destroyOnClose) {
                    destroy.call(this, ...args)
                }

                afterClose?.()
            },
        }

        render(currentConfig)
    }

    if (config.routerChangeClose) {
        _CreateApiDestroyFns.push(destroy)
    }

    render(currentConfig)

    return {
        update,
        close,
        destroy,
    }
}

/**
 * 创建带有ref的API组件
 * @param Component - 要渲染的组件
 * @param config - 配置
 * @returns 包裹后的组件
 */
export const refAPI = (
    Component: React.ComponentType<RefAPIProps>,
    config: any = {},
): React.ForwardRefExoticComponent<any> => {
    return forwardRef((_, ref) => {
        const [ctrl, setCtrl] = useState<RefAPIProps>({
            visible: false,
        })

        useImperativeHandle(ref, () => ({
            setCtrl,
        }))

        const props = {
            ...ctrl,
            onCancel() {
                setCtrl(prev => ({
                    ...prev,
                    visible: false,
                }))
            },
        }

        return <Component {...config} {...props} />
    })
}

/**
 * 创建基于Promise的API
 * @param Component - 要渲染的组件
 * @param extra - 额外配置
 * @param extra.resolveKey - 确定键
 * @param extra.rejectKey - 取消键
 * @param extra.beforeResolveKey - resolve前执行的方法
 * @returns 返回一个函数，调用后返回Promise
 */
export const promiseAPI = (
    Component: React.ComponentType<any>,
    extra: { resolveKey?: string; rejectKey?: string; beforeResolveKey?: string } = {
        resolveKey: 'onOk',
        rejectKey: 'onCancel',
        beforeResolveKey: 'wainFunc',
    },
): ((options?: any) => Promise<any>) => {
    return (options = {}) =>
        new Promise((resolve, reject) => {
            const { resolveKey = 'onOk', rejectKey = 'onCancel', beforeResolveKey = 'wainFunc' } = extra

            const modal = createAPI(Component, {
                routerChangeClose: true,
                ...(options || {}),
                async [resolveKey](e: any) {
                    try {
                        let waitRes = null

                        if (typeof options[beforeResolveKey] === 'function') {
                            const res =
                                typeof options.resolveWaitRes === 'function'
                                    ? options[beforeResolveKey](
                                          options.resolveWaitRes(e, {
                                              close: modal?.close,
                                              reject,
                                          }),
                                      )
                                    : options[beforeResolveKey]()

                            //  boolean
                            if (typeof res === 'boolean') {
                                waitRes = res
                            }

                            // promise
                            if (res && typeof res.then === 'function') {
                                waitRes = await res
                            }
                        }

                        modal?.close()
                        resolve({
                            data: e,
                            waitRes,
                            modal: {
                                close: modal?.close,
                                reject,
                            },
                        })
                    } catch (e) {
                        console.error('catchError', e)
                    }

                    return Promise.resolve()
                },
                [rejectKey]() {
                    reject(new Error('cancel'))
                    return Promise.resolve()
                },
            })
        })
}

const DEFAULT_ZINDEX = 1100

/**
 * 获取当前模态框最大z-index
 * @returns 当前最大z-index值
 */
export const getCurrentModalMaxZindex = (): number => {
    const allEls = Array.from(document.querySelectorAll('.ant-modal-wrap')).concat(
        Array.from(document.querySelectorAll('.ant-drawer')),
    )

    const zIndexList = allEls
        .filter(Boolean)
        .map(v => {
            const elStyles = window.getComputedStyle(v)
            return Number(elStyles?.zIndex)
        })
        .filter(Boolean)

    console.info('getCurrentModalMaxZindex:', zIndexList)

    if (zIndexList.length) {
        const maxZindex = Math.max.apply(null, zIndexList)
        return maxZindex + 1
    }

    return DEFAULT_ZINDEX
}

/**
 * 检查元素是否在视口中
 * @param el - 要检查的元素
 * @returns 是否在视口中
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function isElementInViewport(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect()

    const windHeight = window.innerHeight || document.documentElement.clientHeight
    const winWidth = window.innerWidth || document.documentElement.clientWidth

    return (
        rect.top >= 0 &&
        rect.top < windHeight &&
        rect.left >= 0 &&
        rect.left < winWidth &&
        rect.bottom <= windHeight &&
        rect.right <= winWidth
    )
}

// 创建默认的确认模态框
export const confirmModal = promiseAPI(props => {
    const footer = (
        <>
            <ActionButton onClick={props?.onCancel} {...(props?.cancelButtonProps || {})}>
                {props?.cancelText || '取消'}
            </ActionButton>
            <ActionButton type={props?.okType || 'primary'} onClick={props?.onOk} {...(props?.okButtonProps || {})}>
                {props?.okText || '确定'}
            </ActionButton>
        </>
    )

    return <Modal title="提示" footer={footer} {...props} />
})
